import React, { useEffect, useState } from "react";
import { Button, Input, Select, DatePicker, Row, Col, Form, Card } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";

const { TextArea } = Input;

const AddEquipment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const [loader, setLoader] = useState(false);
    const [ships, setShips] = useState([]);
    const editData = location.state?.editData;
    const viewOnly = location.state?.viewOnly;

    useEffect(() => {
        fetchShips();
        if (editData) {
            fetchEquipmentDetails(editData.id || editData.equipment_id);
        }
    }, [editData]);

    const fetchEquipmentDetails = async (id) => {
        setLoader(true);
        try {
            const FORM_DATA = new FormData();
            FORM_DATA.append("equipment_id", id);
            FORM_DATA.append("id", id);
            const res = await post(ApiURL.view_equipment, FORM_DATA);
            if (res?.status === true) {
                const data = res?.data || res?.response_data || editData;
                form.setFieldsValue({
                    ship_id: data.ship_id,
                    name: data.name,
                    category: data.category,
                    manufacturer: data.manufacturer,
                    model: data.model,
                    serial_number: data.serial_number,
                    location_on_vessel: data.location_on_vessel,
                    manual_reference: data.manual_reference,
                    installation_date: data.installation_date ? dayjs(data.installation_date) : null,
                    is_active: data.is_active === true || data.is_active === 1 || data.is_active === "active" ? "active" : "inactive",
                });
            }
        } catch (error) {
            console.error("Error fetching equipment details:", error);
            form.setFieldsValue({
                ship_id: editData.ship_id,
                name: editData.name,
                category: editData.category,
                manufacturer: editData.manufacturer,
                model: editData.model,
                serial_number: editData.serial_number,
                location_on_vessel: editData.location_on_vessel,
                manual_reference: editData.manual_reference,
                installation_date: editData.installation_date ? dayjs(editData.installation_date) : null,
                is_active: editData.is_active === true || editData.is_active === 1 || editData.is_active === "active" ? "active" : "inactive",
            });
        } finally {
            setLoader(false);
        }
    };

    const fetchShips = async () => {
        try {
            const FORM_DATA = new FormData();
            const res = await post(ApiURL.list_ships_view_com, FORM_DATA);
            if (res?.status === true) {
                setShips(res?.ships || []);
            }
        } catch (error) {
            console.error("Error fetching ships:", error);
        }
    };

    const onFinish = async (values) => {
        setLoader(true);

        try {
            const FORM_DATA = new FormData();
            const user_Data = JSON.parse(localStorage.getItem("user_data"));

            if (editData) {
                const id = editData.id || editData.equipment_id;
                FORM_DATA.append("equipment_id", id);
                FORM_DATA.append("id", id);
            }
            FORM_DATA.append("name", values.name);
            FORM_DATA.append("category", values.category);
            FORM_DATA.append("ship_id", values.ship_id || "");
            FORM_DATA.append("company_id", user_Data?.data?.company?.company_id);
            FORM_DATA.append("manufacturer", values.manufacturer || "");
            FORM_DATA.append("model", values.model || "");
            FORM_DATA.append("serial_number", values.serial_number || "");
            FORM_DATA.append("location_on_vessel", values.location_on_vessel || "");
            FORM_DATA.append("manual_reference", values.manual_reference || "");
            FORM_DATA.append("installation_date", values.installation_date ? values.installation_date.format("YYYY-MM-DD") : "");
            FORM_DATA.append("is_active", values.is_active === "active" ? 1 : 0);
            
            FORM_DATA.append("user_id", user_Data?.data?.user_id);

            const apiEndpoint = editData ? ApiURL.update_equipment : ApiURL.add_equipment;
            const res = await post(apiEndpoint, FORM_DATA);
            if (res?.status === true) {
                SuccessToast("Success", editData ? "Equipment updated successfully!" : "Equipment added successfully!");
                navigate("/equipment-register");
            } else {
                ErrorToast("Error", res?.message || `Failed to ${editData ? 'update' : 'add'} equipment.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            ErrorToast("Error", "Something went wrong during submission.");
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className="white-container" style={{ padding: "10px" }}>
            <div className="row" style={{ padding: "10px", marginBottom: "10px" }}>
                <div className="col-6">
                    <div className="theme-content-title text-left">
                        <h3 style={{ margin: 0 }}>
                            {viewOnly ? "View Equipment" : (editData ? "Edit Equipment" : "Add Equipment")}
                        </h3>
                    </div>
                </div>
                <div className="col-6" style={{ textAlign: "right" }}>
                    <Button onClick={() => navigate("/equipment-register")}>Back to List</Button>
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ is_active: "active", installation_date: dayjs() }}
                disabled={viewOnly}
            >
                <Card
                    title={<span style={{ fontWeight: 600 }}>Equipment Details</span>}
                    style={{ marginBottom: "20px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                >
                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="ship_id"
                                label="Select Ship"
                            >
                                <Select
                                    placeholder="Select Ship"
                                    allowClear
                                    options={ships.map((ship) => ({ label: ship.vessel_name, value: ship.id }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="name"
                                label="Equipment Name"
                                rules={[{ required: true, message: "Please enter equipment name" }]}
                            >
                                <Input placeholder="Enter equipment name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{ required: true, message: "Please select category" }]}
                            >
                                <Select placeholder="Select category">
                                    <Select.Option value="Navigation">Navigation</Select.Option>
                                    <Select.Option value="Fire Safety">Fire Safety</Select.Option>
                                    <Select.Option value="Comms">Comms</Select.Option>
                                    <Select.Option value="Safety">Safety</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="manufacturer" label="Manufacturer">
                                <Input placeholder="Enter manufacturer" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="model" label="Model">
                                <Input placeholder="Enter model" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="serial_number" label="Serial Number">
                                <Input placeholder="Enter serial number" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="location_on_vessel" label="Location on Vessel">
                                <Input placeholder="Enter location (e.g. Engine Room)" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="manual_reference" label="Manual Reference">
                                <Input placeholder="Enter manual reference" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="installation_date" label="Installation Date">
                                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="is_active" label="Status">
                                <Select>
                                    <Select.Option value="active">Active</Select.Option>
                                    <Select.Option value="inactive">Inactive</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {!viewOnly && (
                    <div style={{ textAlign: "center", marginTop: "40px", marginBottom: "30px" }}>
                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            loading={loader}
                            className="save_btn"
                            style={{
                                height: "48px",
                                padding: "0 80px",
                                borderRadius: "24px",
                                fontWeight: 600,
                                fontSize: "16px",
                            }}
                        >
                            {editData ? "Update Equipment" : "Save Equipment"}
                        </Button>
                    </div>
                )}
            </Form>
        </div>
    );
};

export default AddEquipment;
